"use strict";
/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2020 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseRequestHandler = void 0;
const axios_1 = __importDefault(require("axios"));
const ApiError_1 = require("../cart/requestHandlers/ApiError");
const CartError_1 = require("../cart/requestHandlers/CartError");
const axios = axios_1.default.create({
    baseURL: "https://api.connox.io/v1/",
    responseType: "json",
    timeout: 20000,
    withCredentials: false,
});
const axFileRequest = axios_1.default.create({
    method: "get",
    responseType: "blob",
    timeout: 20000,
    withCredentials: false,
});
class BaseRequestHandler {
    static performFileRequest(path) {
        return __awaiter(this, void 0, void 0, function* () {
            let responseBlob;
            let responseString;
            try {
                responseBlob = yield axFileRequest.request({ url: path });
                const readFile = (file) => new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsText(file);
                });
                responseString = yield readFile(responseBlob.data);
            }
            catch (e) {
                throw new ApiError_1.ApiError("An error occurred while downloading the .pem file", e);
            }
            return responseString;
        });
    }
    /**
     * Default request method. Returns Promise<T> or throws
     * an error when success == false or no result returned
     * @param method "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
     * @param path api path
     * @param config params of type U wrapped in an object of type IBaseRequestConfiguration
     */
    static performRequest(method, path, config) {
        return __awaiter(this, void 0, void 0, function* () {
            let response;
            const axiosConfig = Object.assign(Object.assign({}, config), { method, url: path });
            try {
                response = yield axios.request(axiosConfig);
            }
            catch (e) {
                throw new ApiError_1.ApiError("An error occurred executing the request", e);
            }
            const result = response.data;
            if (!result.success) {
                if (!result.message) {
                    throw new ApiError_1.ApiError("An unknown error occurred", result);
                }
                else {
                    throw new CartError_1.CartError(result.message);
                }
            }
            return result.result;
        });
    }
    static performRequestWithPossibleError(method, path, config) {
        return __awaiter(this, void 0, void 0, function* () {
            let response;
            const axiosConfig = Object.assign(Object.assign({}, config), { method, url: path });
            try {
                response = yield axios.request(axiosConfig);
            }
            catch (e) {
                return new ApiError_1.ApiError("An error occurred executing the request", e);
            }
            const result = response.data;
            if (!result.success) {
                if (!result.message) {
                    return new ApiError_1.ApiError("An unknown error occurred", result);
                }
                else {
                    return new CartError_1.CartError(result.message);
                }
            }
            if (!result.result) {
                return new ApiError_1.ApiError("The request result was malformed", result);
            }
            return result.result;
        });
    }
    /**
     * same as 'performRequest' but no check on `success` or `result`
     * also capable of sending form data content type
     * @param method "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
     * @param path api path
     * @param baseURL api location
     * @param config params of type U wrapped in an object of type IBaseRequestConfiguration
     */
    static performRequestNoResult(method, path, baseURL, config) {
        return __awaiter(this, void 0, void 0, function* () {
            let response;
            const axiosConfig = Object.assign(Object.assign({}, config), { method, url: path });
            if (baseURL) {
                axiosConfig.baseURL = baseURL;
            }
            if (config && config.data && config.data instanceof FormData) {
                axiosConfig.headers = {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "accept": "text/html,application/xhtml+xml,application/xml",
                };
            }
            try {
                response = yield axios.request(axiosConfig);
            }
            catch (err) {
                throw new ApiError_1.ApiError("An error occurred executing form request", err);
            }
            if (!response.data.success) {
                throw new ApiError_1.ApiError("Request was not successful", response.data);
            }
            return response.data;
        });
    }
}
exports.BaseRequestHandler = BaseRequestHandler;
